/**
 * CardChoicePage (08/2026) — /karten-wahl/:token
 * Öffentliche Seite: Kunde wählt Karte hinterlegen (Payrexx) ODER
 * weiterhin Rechnung/Karte/TWINT nach dem Training.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CreditCard, ShieldCheck, CheckCircle2, Loader2, AlertTriangle, Receipt, Zap, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CardChoicePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/card-choice/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Link ungültig oder abgelaufen'));
  }, [token]);

  const choose = async (choice) => {
    setSubmitting(choice);
    try {
      const r = await axios.post(`${API}/public/card-choice/${token}/choose`, { choice });
      if (choice === 'card' && r.data.payment_link) {
        window.location.href = r.data.payment_link;
        return;
      }
      setDone(choice);
    } catch (e) {
      setError(e.response?.data?.detail || 'Fehler — bitte später erneut versuchen');
    } finally {
      setSubmitting(null);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="text-2xl font-black tracking-widest">STRONGFIT</div>
          <div className="text-xs text-yellow-400 tracking-widest mt-1">BEQUEM BEZAHLEN</div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-2xl">{children}</div>
        <p className="text-center text-xs text-zinc-600 mt-4">StrongFit GmbH · Sichere Zahlungsabwicklung über Payrexx (Schweiz)</p>
      </div>
    </div>
  );

  if (error) {
    return (
      <Shell>
        <div className="text-center py-8" data-testid="card-choice-error">
          <AlertTriangle className="mx-auto mb-3 text-amber-400" size={40} />
          <p className="text-zinc-300">{error}</p>
        </div>
      </Shell>
    );
  }

  if (!info) {
    return (
      <Shell>
        <div className="text-center py-12"><Loader2 className="mx-auto animate-spin text-zinc-500" size={32} /></div>
      </Shell>
    );
  }

  if (done === 'invoice' || info.declined) {
    return (
      <Shell>
        <div className="text-center py-8" data-testid="card-choice-invoice-confirmed">
          <CheckCircle2 className="mx-auto mb-3 text-yellow-400" size={44} />
          <h2 className="text-lg font-bold mb-2">Alles klar, {info.first_name}!</h2>
          <p className="text-zinc-400 text-sm">
            Sie bezahlen weiterhin per Rechnung/Karte/TWINT nach dem Training.
            Sie erhalten keine weitere Anfrage von uns. Falls Sie es sich anders
            überlegen, melden Sie sich jederzeit im Studio.
          </p>
        </div>
      </Shell>
    );
  }

  if (info.card_added) {
    return (
      <Shell>
        <div className="text-center py-8" data-testid="card-choice-already-done">
          <CheckCircle2 className="mx-auto mb-3 text-yellow-400" size={44} />
          <h2 className="text-lg font-bold mb-2">Ihre Karte ist bereits hinterlegt</h2>
          <p className="text-zinc-400 text-sm">Es ist nichts weiter zu tun — Ihre Trainings werden automatisch abgerechnet.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-xl font-bold mb-1" data-testid="card-choice-title">Hallo {info.first_name} 👋</h1>
      <p className="text-zinc-400 text-sm mb-5">
        Wie möchten Sie Ihre Trainings künftig bezahlen? Wählen Sie Ihre bevorzugte Variante:
      </p>

      <div className="bg-zinc-800/60 border border-zinc-700 rounded-xl p-4 mb-3">
        <div className="flex items-center gap-2 mb-2 font-semibold text-yellow-400">
          <Zap size={18} /> Karte hinterlegen — die bequemste Variante
        </div>
        <ul className="text-sm text-zinc-300 space-y-1.5 mb-3">
          <li className="flex gap-2"><CheckCircle2 size={16} className="text-yellow-400 mt-0.5 shrink-0" /> Keine Rechnungen mehr — alles läuft automatisch nach dem Training</li>
          <li className="flex gap-2"><CheckCircle2 size={16} className="text-yellow-400 mt-0.5 shrink-0" /> Kein Zahlungsaufwand, keine Mahnungen, nichts vergessen</li>
          <li className="flex gap-2"><CheckCircle2 size={16} className="text-yellow-400 mt-0.5 shrink-0" /> Jederzeit im Studio widerrufbar</li>
        </ul>
        <div className="flex items-start gap-2 bg-yellow-950/30 border border-yellow-800/60 rounded-lg p-3 mb-4">
          <ShieldCheck size={20} className="text-yellow-400 shrink-0 mt-0.5" />
          <p className="text-xs text-yellow-100">
            <strong>Ihre Sicherheit:</strong> Ihre Karte wird <strong>nicht im StrongFit-System</strong> gespeichert,
            sondern sicher beim <strong>Schweizer Zahlungsanbieter Payrexx</strong> — zertifiziert und PCI-DSS-konform.
            StrongFit hat keinen Zugriff auf Ihre Kartendaten.
          </p>
        </div>
        <Button
          onClick={() => choose('card')}
          disabled={!!submitting}
          data-testid="choose-card-btn"
          className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-semibold h-12 text-base"
        >
          {submitting === 'card' ? <Loader2 className="animate-spin mr-2" size={18} /> : <CreditCard className="mr-2" size={18} />}
          Karte jetzt sicher hinterlegen
        </Button>
      </div>

      <div className="bg-zinc-800/30 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-2 font-semibold text-zinc-300">
          <Receipt size={18} /> Oder wie bisher
        </div>
        <p className="text-xs text-zinc-500 mb-3 flex items-start gap-2">
          <Clock size={14} className="mt-0.5 shrink-0" />
          Sie erhalten nach jedem Training Ihre Rechnung und bezahlen per Rechnung, Karte oder TWINT vor Ort.
        </p>
        <Button
          onClick={() => choose('invoice')}
          disabled={!!submitting}
          variant="outline"
          data-testid="choose-invoice-btn"
          className="w-full border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800 hover:text-white h-auto min-h-11 py-2.5 whitespace-normal text-sm"
        >
          {submitting === 'invoice' && <Loader2 className="animate-spin mr-2" size={16} />}
          Ich bezahle weiterhin per Rechnung/Karte/TWINT nach dem Training
        </Button>
      </div>
    </Shell>
  );
}
